import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { FiKey, FiPlus, FiEdit2, FiTrash2, FiX, FiSave, FiLink, FiSearch, FiCopy, FiCheck, FiPackage, FiCalendar, FiArrowLeft, FiShield } from 'react-icons/fi';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function maskKey(key) {
  if (!key || key.length < 8) return key || '';
  return key.slice(0, 4) + '••••' + key.slice(-4);
}

const CARD_COLORS = [
  { bg: 'rgba(37,99,235,0.08)', border: 'rgba(37,99,235,0.2)', accent: '#2563eb' },
  { bg: 'rgba(16,185,129,0.08)', border: 'rgba(16,185,129,0.2)', accent: '#10b981' },
  { bg: 'rgba(139,92,246,0.08)', border: 'rgba(139,92,246,0.2)', accent: '#8b5cf6' },
  { bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.2)', accent: '#f59e0b' },
  { bg: 'rgba(236,72,153,0.08)', border: 'rgba(236,72,153,0.2)', accent: '#ec4899' },
  { bg: 'rgba(6,182,212,0.08)', border: 'rgba(6,182,212,0.2)', accent: '#06b6d4' },
];

export default function Licences() {
  const { authFetch } = useAuth();
  const [licences, setLicences] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [selectedLic, setSelectedLic] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: '', vendor: '', license_key: '', asset_id: 0 });
  const [search, setSearch] = useState('');
  const [copied, setCopied] = useState(false);
  const [assetSearch, setAssetSearch] = useState('');
  const [showAssetDropdown, setShowAssetDropdown] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3000); };
  const toastEl = (
    <AnimatePresence>
      {toast && (
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
          style={{ position: 'fixed', top: 20, right: 20, zIndex: 999, padding: '12px 20px', borderRadius: 10,
            background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#16a34a',
            fontSize: 13, fontWeight: 600, boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            display: 'flex', alignItems: 'center', gap: 8
          }}>
          <FiCheck size={16} /> {toast}
        </motion.div>
      )}
    </AnimatePresence>
  );

  useEffect(() => {
    Promise.all([
      authFetch('/api/assets/licenses/all').then(r => r.ok ? r.json() : []).catch(() => []),
      authFetch('/api/assets?limit=200').then(r => r.ok ? r.json() : {}).catch(() => ({})),
    ]).then(([licData, assetRes]) => {
      setLicences(licData);
      setAssets(assetRes && assetRes.data ? assetRes.data : (Array.isArray(assetRes) ? assetRes : []));
      setLoading(false);
    });
  }, []);

  const handleAdd = async () => {
    if (!form.name.trim()) return;
    const res = await authFetch('/api/assets/licenses/create', {
      method: 'POST', body: JSON.stringify(form)
    });
    if (res.ok) {
      const lic = await res.json();
      setLicences(prev => [lic, ...prev]);
      setForm({ name: '', vendor: '', license_key: '', asset_id: 0 });
      setShowAdd(false);
      showToast('Licence created');
    }
  };

  const handleUpdate = async () => {
    if (!form.name.trim() || !selectedLic) return;
    const res = await authFetch(`/api/assets/licenses/${selectedLic.id}`, {
      method: 'PUT', body: JSON.stringify(form)
    });
    if (res.ok) {
      const updated = await res.json();
      setLicences(prev => prev.map(l => l.id === selectedLic.id ? updated : l));
      setSelectedLic(updated);
      setEditing(false);
      showToast('Licence updated');
    }
  };

  const handleAssign = async (assetId) => {
    if (!selectedLic) return;
    const res = await authFetch(`/api/assets/licenses/${selectedLic.id}/assign`, {
      method: 'PATCH', body: JSON.stringify({ asset_id: assetId })
    });
    if (res.ok) {
      const updated = await res.json();
      setLicences(prev => prev.map(l => l.id === selectedLic.id ? updated : l));
      setSelectedLic(updated);
      showToast(assetId ? 'Licence assigned to asset' : 'Licence unassigned');
    }
  };

  const handleDelete = async (id) => {
    const lic = licences.find(l => l.id === id);
    setConfirmDialog({
      title: 'Delete Licence', rows: [{ label: 'Licence', value: lic?.name || `#${id}` }],
      confirmLabel: 'Delete', confirmColor: '#dc2626',
      onConfirm: async () => {
        const res = await authFetch(`/api/assets/licenses/${id}`, { method: 'DELETE' });
        if (res.ok) { setLicences(prev => prev.filter(l => l.id !== id)); setSelectedLic(null); showToast('Licence deleted'); }
        setConfirmDialog(null);
      }
    });
  };

  const copyKey = (key) => {
    navigator.clipboard.writeText(key).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const filtered = useMemo(() => {
    let result = [...licences];
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(l =>
        l.name?.toLowerCase().includes(s) || l.vendor?.toLowerCase().includes(s) ||
        l.license_key?.toLowerCase().includes(s) || l.asset_name?.toLowerCase().includes(s)
      );
    }
    return result.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [licences, search]);

  if (loading) {
    return (
      <div>
        <div className="skeleton" style={{ width: 140, height: 28, marginBottom: 8 }} />
        <div className="skeleton" style={{ width: 220, height: 14, marginBottom: 24 }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {[...Array(6)].map((_, i) => (
            <div key={i} className="skeleton" style={{ height: 160, borderRadius: 14 }} />
          ))}
        </div>
      </div>
    );
  }

  // Detail view for selected licence
  if (selectedLic) {
    const color = CARD_COLORS[selectedLic.id % CARD_COLORS.length];
    return (
      <div>
        {toastEl}
        <button onClick={() => { setSelectedLic(null); setEditing(false); }}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none',
            color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 14, fontWeight: 500,
            padding: 0, marginBottom: 20
          }}>
          <FiArrowLeft size={16} /> Back to Licences
        </button>

        <div style={{
          background: 'var(--bg-card)', borderRadius: 16, border: '1px solid var(--border)',
          overflow: 'hidden'
        }}>
          {/* Header */}
          <div style={{
            padding: '20px 16px', background: color.bg,
            borderBottom: `1px solid ${color.border}`
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 12, background: color.accent,
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <FiShield size={24} color="#fff" />
                </div>
                <div>
                  {editing ? (
                    <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      style={{ ...inputStyle, fontSize: 20, fontWeight: 700, padding: '6px 12px' }} />
                  ) : (
                    <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>{selectedLic.name}</h2>
                  )}
                  {editing ? (
                    <input value={form.vendor} onChange={e => setForm(f => ({ ...f, vendor: e.target.value }))}
                      placeholder="Vendor" style={{ ...inputStyle, fontSize: 13, marginTop: 4, padding: '4px 10px' }} />
                  ) : (
                    selectedLic.vendor && <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginTop: 2 }}>{selectedLic.vendor}</div>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {editing ? (
                  <>
                    <button onClick={handleUpdate} style={{
                      display: 'flex', alignItems: 'center', gap: 4, padding: '8px 16px', borderRadius: 8,
                      border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600
                    }}><FiSave size={14} /> Save</button>
                    <button onClick={() => setEditing(false)} style={{
                      padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)',
                      background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13
                    }}>Cancel</button>
                  </>
                ) : (
                  <>
                    <button onClick={() => { setEditing(true); setForm({ name: selectedLic.name, vendor: selectedLic.vendor || '', license_key: selectedLic.license_key || '' }); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 4, padding: '8px 14px', borderRadius: 8,
                        border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer',
                        fontSize: 13, fontWeight: 500, color: 'var(--text-primary)'
                      }}><FiEdit2 size={14} /> Edit</button>
                    <button onClick={() => handleDelete(selectedLic.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 4, padding: '8px 14px', borderRadius: 8,
                        border: '1px solid rgba(220,38,38,0.2)', background: 'rgba(220,38,38,0.06)', cursor: 'pointer',
                        fontSize: 13, fontWeight: 500, color: '#dc2626'
                      }}><FiTrash2 size={14} /> Delete</button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Content */}
          <div style={{ padding: '20px 16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16, marginBottom: 24 }}>
              {/* Licence Key */}
              <div style={{
                padding: 20, borderRadius: 12, background: 'var(--bg-input)', border: '1px solid var(--border)'
              }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <FiKey size={10} /> Licence Key
                </div>
                {editing ? (
                  <input value={form.license_key} onChange={e => setForm(f => ({ ...f, license_key: e.target.value }))}
                    placeholder="XXXX-XXXX-XXXX" style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 15 }} />
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <code style={{
                      fontSize: 15, fontWeight: 600, color: 'var(--text-primary)',
                      wordBreak: 'break-all', flex: 1
                    }}>
                      {selectedLic.license_key || '—'}
                    </code>
                    {selectedLic.license_key && (
                      <button onClick={() => copyKey(selectedLic.license_key)}
                        style={{
                          padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)',
                          background: 'var(--bg-card)', cursor: 'pointer', color: copied ? '#10b981' : 'var(--text-muted)',
                          display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, flexShrink: 0
                        }}>
                        {copied ? <><FiCheck size={12} /> Copied</> : <><FiCopy size={12} /> Copy</>}
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Assigned Asset */}
              <div style={{
                padding: 20, borderRadius: 12, background: 'var(--bg-input)', border: '1px solid var(--border)'
              }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <FiLink size={10} /> Assigned To
                </div>
                {selectedLic.asset_id && selectedLic.asset_id !== 0 && !showAssetDropdown ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ flex: 1, fontSize: 14, fontWeight: 600, color: '#2563eb' }}>
                      {selectedLic.asset_tag} — {selectedLic.asset_name}
                    </div>
                    <button onClick={() => { setShowAssetDropdown(true); setAssetSearch(''); }}
                      style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', fontSize: 11, color: 'var(--text-secondary)' }}>
                      Change
                    </button>
                    <button onClick={() => handleAssign(0)}
                      style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(220,38,38,0.2)', background: 'rgba(220,38,38,0.06)', cursor: 'pointer', fontSize: 11, color: '#dc2626' }}>
                      Remove
                    </button>
                  </div>
                ) : (
                  <div style={{ position: 'relative' }}>
                    <div style={{ position: 'relative' }}>
                      <FiSearch size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                      <input
                        value={assetSearch}
                        onChange={e => { setAssetSearch(e.target.value); setShowAssetDropdown(true); }}
                        onFocus={() => setShowAssetDropdown(true)}
                        placeholder="Search assets..."
                        style={{
                          width: '100%', padding: '10px 12px 10px 32px', borderRadius: 8, border: '1px solid var(--border)',
                          fontSize: 14, color: 'var(--text-primary)', background: 'var(--bg-card)',
                          outline: 'none', boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    {showAssetDropdown && (
                      <div style={{
                        position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, marginTop: 4,
                        background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8,
                        maxHeight: 200, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
                      }}>
                        {assets
                          .filter(a => {
                            if (!assetSearch) return true;
                            const s = assetSearch.toLowerCase();
                            return a.name?.toLowerCase().includes(s) || a.tag?.toLowerCase().includes(s);
                          })
                          .slice(0, 20)
                          .map(a => (
                            <div key={a.id}
                              onClick={() => { handleAssign(a.id); setShowAssetDropdown(false); setAssetSearch(''); }}
                              style={{
                                padding: '8px 12px', cursor: 'pointer', fontSize: 13,
                                color: 'var(--text-primary)', borderBottom: '1px solid var(--border)'
                              }}
                              onMouseEnter={e => e.currentTarget.style.background = 'rgba(37,99,235,0.06)'}
                              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                            >
                              <span style={{ fontWeight: 600 }}>{a.tag}</span> — {a.name}
                            </div>
                          ))}
                        {assets.filter(a => {
                          if (!assetSearch) return true;
                          const s = assetSearch.toLowerCase();
                          return a.name?.toLowerCase().includes(s) || a.tag?.toLowerCase().includes(s);
                        }).length === 0 && (
                          <div style={{ padding: '12px', fontSize: 13, color: 'var(--text-muted)', textAlign: 'center' }}>No assets found</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
              {/* Vendor */}
              <div style={{
                padding: 20, borderRadius: 12, background: 'var(--bg-input)', border: '1px solid var(--border)'
              }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <FiPackage size={10} /> Vendor
                </div>
                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>
                  {selectedLic.vendor || '—'}
                </div>
              </div>

              {/* Created */}
              <div style={{
                padding: 20, borderRadius: 12, background: 'var(--bg-input)', border: '1px solid var(--border)'
              }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <FiCalendar size={10} /> Created
                </div>
                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>
                  {formatDate(selectedLic.created_at)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {toastEl}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--text-primary)' }}>Licences</h1>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: 'var(--text-secondary)' }}>{licences.length} total licences · licence keys encrypted at rest</p>
        </div>
        <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
          onClick={() => { setShowAdd(true); setForm({ name: '', vendor: '', license_key: '', asset_id: 0 }); }}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px',
            borderRadius: 10, border: 'none', background: '#2563eb', color: '#fff',
            cursor: 'pointer', fontSize: 14, fontWeight: 600, boxShadow: '0 2px 8px rgba(37,99,235,0.3)'
          }}
        ><FiPlus size={16} /> Add Licence</motion.button>
      </div>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: 20 }}>
        <FiSearch size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search licences..."
          style={{
            width: '100%', padding: '10px 12px 10px 36px', borderRadius: 10,
            border: '1px solid var(--border)', fontSize: 14, outline: 'none',
            background: 'var(--bg-card)', boxSizing: 'border-box', color: 'var(--text-primary)'
          }}
        />
      </div>

      {/* Add Form */}
      <AnimatePresence>
        {showAdd && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            style={{
              background: 'var(--bg-card)', borderRadius: 14, padding: 24, border: '2px solid #2563eb',
              marginBottom: 20
            }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>New Licence</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginBottom: 16 }}>
              <div>
                <label style={labelStyle}>Name *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Microsoft Office" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Vendor</label>
                <input value={form.vendor} onChange={e => setForm(f => ({ ...f, vendor: e.target.value }))}
                  placeholder="e.g. Microsoft" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Licence Key</label>
                <input value={form.license_key} onChange={e => setForm(f => ({ ...f, license_key: e.target.value }))}
                  placeholder="XXXX-XXXX-XXXX" style={{ ...inputStyle, fontFamily: 'monospace' }} />
              </div>
              <div>
                <label style={labelStyle}>Assign to Asset</label>
                <select value={form.asset_id} onChange={e => setForm(f => ({ ...f, asset_id: parseInt(e.target.value) }))}
                  style={inputStyle}>
                  <option value={0}>Unassigned</option>
                  {assets.map(a => <option key={a.id} value={a.id}>{a.tag} — {a.name}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleAdd} style={{
                padding: '8px 20px', borderRadius: 8, border: 'none', background: '#2563eb',
                color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: 4
              }}><FiSave size={14} /> Save</button>
              <button onClick={() => setShowAdd(false)} style={{
                padding: '8px 20px', borderRadius: 8, border: '1px solid var(--border)',
                background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 13
              }}>Cancel</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Licence Cards Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
        {filtered.map((lic, i) => {
          const color = CARD_COLORS[lic.id % CARD_COLORS.length];
          return (
            <motion.div key={lic.id}
              whileHover={{ scale: 1.01, y: -2 }}
              onClick={() => setSelectedLic(lic)}
              style={{
                background: 'var(--bg-card)', borderRadius: 14, border: '1px solid var(--border)',
                padding: 20, cursor: 'pointer', transition: 'box-shadow 0.2s',
                position: 'relative', overflow: 'hidden'
              }}
            >
              {/* Accent bar */}
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, height: 3,
                background: color.accent
              }} />

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10, background: color.bg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  border: `1px solid ${color.border}`
                }}>
                  <FiShield size={18} color={color.accent} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 15, fontWeight: 700, color: 'var(--text-primary)',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                  }}>{lic.name}</div>
                  {lic.vendor && (
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{lic.vendor}</div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{
                  padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                  background: lic.asset_id && lic.asset_id !== 0 ? 'rgba(37,99,235,0.08)' : 'rgba(100,100,100,0.08)',
                  color: lic.asset_id && lic.asset_id !== 0 ? '#2563eb' : 'var(--text-muted)',
                  display: 'flex', alignItems: 'center', gap: 4,
                  maxWidth: '60%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                }}>
                  <FiLink size={9} />
                  {lic.asset_id && lic.asset_id !== 0
                    ? `${lic.asset_tag || ''} ${lic.asset_name || ''}`.trim()
                    : 'Unassigned'}
                </div>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{formatDate(lic.created_at)}</span>
              </div>
            </motion.div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
          <FiKey size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
          <div style={{ fontSize: 15 }}>{search ? 'No matching licences' : 'No licences yet'}</div>
        </div>
      )}

      {/* Themed Confirmation Dialog */}
      <AnimatePresence>
        {confirmDialog && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setConfirmDialog(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 300 }}>
            <motion.div initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
              onClick={e => e.stopPropagation()}
              style={{ background: 'var(--bg-card)', borderRadius: 16, width: '100%', maxWidth: 440, border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)', overflow: 'hidden' }}>
              <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{confirmDialog.title}</h3>
                <button onClick={() => setConfirmDialog(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
              </div>
              <div style={{ padding: '18px 22px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {confirmDialog.rows.map((row, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, fontSize: 13 }}>
                      <span style={{ fontWeight: 600, color: 'var(--text-secondary)', minWidth: 110, flexShrink: 0 }}>{row.label}</span>
                      <span style={{ color: 'var(--text-primary)', wordBreak: 'break-word' }}>{row.value}</span>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
                  <button onClick={() => setConfirmDialog(null)}
                    style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
                    Cancel
                  </button>
                  <motion.button whileTap={{ scale: 0.97 }} onClick={confirmDialog.onConfirm}
                    style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: confirmDialog.confirmColor || 'var(--accent)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                    {confirmDialog.confirmLabel || 'Confirm'}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const labelStyle = {
  display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)',
  marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.3px'
};

const inputStyle = {
  width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)',
  fontSize: 13, outline: 'none', boxSizing: 'border-box', background: 'var(--bg-input)',
  fontFamily: 'inherit', color: 'var(--text-primary)'
};
